// pdfDesignConditions.js — Shared "Design Conditions" section for all PDF reports.
//
// Exports three things:
//   buildDesignConditionsRows(project, isSI)      → pure function, no DOM
//   designConditionsHTML(project, isSI)           → HTML string for renderHTMLToPdf
//   drawDesignConditionsToPdf(pdf, project, isSI, startY) → programmatic jsPDF draw,
//                                                             returns new Y cursor

import { toSI } from "./units";
import { isModelN } from "./wizardCalc";

// Safe parse — returns NaN for empty/missing so callers can check with isFinite
function p(v) { return parseFloat(v); }

// Format temperature with 1 decimal in IP or SI.
function fmtT(v, isSI) {
  if (v === "" || v == null || !isFinite(p(v))) return "—";
  const num = isSI ? toSI.temp(v) : p(v);
  return num.toFixed(1);
}

const TU = (isSI) => isSI ? "°C" : "°F";
const LU = (isSI) => isSI ? "m" : "ft";

/**
 * Returns a structured list of design-condition rows for PDF display.
 * Each item: { group: string, label: string, value: string }
 * Groups: "Outdoor" | "Indoor" | "Water" | "Advanced"
 *
 * Only rows that have a non-trivial value are included.
 * Advanced rows are suppressed when unset / at default / not relevant.
 *
 * @param {object} project  — wizard project object
 * @param {boolean} isSI    — display in SI units
 * @param {string|null} [model] — unit model string; when provided, Outdoor rows and
 *   heat-recovery row are suppressed for non-Model-N selections
 * @returns {{ group: string, label: string, value: string }[]}
 */
export function buildDesignConditionsRows(project, isSI = false, model = null) {
  const showOutdoor = model === null || isModelN(model);
  const dc = project?.designConditions || {};
  const adv = project?.advanced || {};
  const rows = [];

  // ---- Outdoor — only for Model N / N-f (PAC draws outdoor air) ----
  if (showOutdoor) {
    const hasOutdoorDB = isFinite(p(dc.outdoor_db_f));
    const hasOutdoorWB = isFinite(p(dc.outdoor_wb_f));
    if (hasOutdoorDB && hasOutdoorWB) {
      rows.push({
        group: "Outdoor",
        label: `Outdoor DB / WB — Summer (${TU(isSI)})`,
        value: `${fmtT(dc.outdoor_db_f, isSI)} / ${fmtT(dc.outdoor_wb_f, isSI)}`,
      });
    } else if (hasOutdoorDB) {
      rows.push({
        group: "Outdoor",
        label: `Outdoor DB — Summer (${TU(isSI)})`,
        value: fmtT(dc.outdoor_db_f, isSI),
      });
    }

    if (isFinite(p(dc.outdoor_db_winter_f))) {
      rows.push({
        group: "Outdoor",
        label: `Outdoor DB — Winter (${TU(isSI)})`,
        value: fmtT(dc.outdoor_db_winter_f, isSI),
      });
    }

    // Design city / ASHRAE reference
    const isOther = dc.country === "__other__";
    const country = isOther ? (dc.country_other || "") : (dc.country || "");
    const city = isOther
      ? (dc.city_other || "")
      : (dc.city && dc.city !== "__manual__" ? dc.city : "");
    const isAshrae = !isOther && city;
    if (city || country) {
      rows.push({
        group: "Outdoor",
        label: "ASHRAE Reference City",
        value: [city, country].filter(Boolean).join(", ") + (isAshrae ? " (ASHRAE)" : ""),
      });
    }

    const altFt = p(dc.altitude_ft);
    if (isFinite(altFt) && altFt >= 0) {
      const altVal = isSI ? toSI.altitude(altFt).toFixed(0) : String(Math.round(altFt));
      rows.push({
        group: "Outdoor",
        label: `Altitude (${LU(isSI)})`,
        value: altVal,
      });
    }
  }

  // ---- Indoor ----
  const hasRoomDB = isFinite(p(dc.room_db_f));
  const hasRoomRH = dc.room_rh != null && dc.room_rh !== "";
  if (hasRoomDB || hasRoomRH) {
    const dbStr = hasRoomDB ? fmtT(dc.room_db_f, isSI) : "—";
    const rhStr = hasRoomRH ? `${dc.room_rh}%` : "—";
    rows.push({
      group: "Indoor",
      label: `Room DB / RH — Summer (${TU(isSI)} / %)`,
      value: `${dbStr} / ${rhStr}`,
    });
  }

  const hasWinterDB = isFinite(p(dc.room_db_winter_f));
  const hasWinterRH = dc.room_rh_winter != null && dc.room_rh_winter !== "";
  if (hasWinterDB || hasWinterRH) {
    const dbStr = hasWinterDB ? fmtT(dc.room_db_winter_f, isSI) : "—";
    const rhStr = hasWinterRH ? `${dc.room_rh_winter}%` : "—";
    rows.push({
      group: "Indoor",
      label: `Room DB / RH — Winter (${TU(isSI)} / %)`,
      value: `${dbStr} / ${rhStr}`,
    });
  }

  // ---- Water ----
  const hasCoolEWT = isFinite(p(dc.water_entering_f));
  const hasHeatEWT = isFinite(p(dc.water_entering_winter_f ?? dc.water_entering_heat_f));
  const coolEWT = hasCoolEWT ? fmtT(dc.water_entering_f, isSI) : "—";
  const heatEWT = hasHeatEWT
    ? fmtT(dc.water_entering_winter_f ?? dc.water_entering_heat_f, isSI)
    : "—";
  rows.push({
    group: "Water",
    label: `EWT Cooling / Heating (${TU(isSI)})`,
    value: `${coolEWT} / ${heatEWT}`,
  });

  const glycolStr = adv.use_glycol
    ? `${adv.glycol_type || ""}${adv.glycol_percentage ? ` ${adv.glycol_percentage}%` : ""}`.trim() || "Yes"
    : "None";
  rows.push({
    group: "Water",
    label: "Glycol",
    value: glycolStr,
  });

  // ---- Advanced (only show when set / non-default) ----

  // Recovery type — only meaningful for ERV/HRV product selections (Model N / N-f)
  if (showOutdoor && adv.recovery_type) {
    rows.push({
      group: "Advanced",
      label: "Heat Recovery Type",
      value: adv.recovery_type,
    });
  }

  // Coil mode — non-condensing dry-coil flag
  if (adv.non_condensing_mode) {
    rows.push({
      group: "Advanced",
      label: "Coil Mode",
      value: "Non-condensing (dry coil)",
    });
  }

  // Air-on overrides (cool / heat)
  const hasCoolAO = adv.air_on_temp_f != null && adv.air_on_temp_f !== "";
  const hasHeatAO = adv.air_on_temp_heating_f != null && adv.air_on_temp_heating_f !== "";
  if (hasCoolAO || hasHeatAO) {
    const coolAO = hasCoolAO ? `${fmtT(adv.air_on_temp_f, isSI)} ${TU(isSI)}` : "auto";
    const heatAO = hasHeatAO ? `${fmtT(adv.air_on_temp_heating_f, isSI)} ${TU(isSI)}` : "auto";
    rows.push({
      group: "Advanced",
      label: "Air-On Override (Cool / Heat)",
      value: `${coolAO} / ${heatAO}`,
    });
  }

  // Length cap per model
  const lenCapByModel = adv.maxLengthByModel || {};
  const lenCapPairs = Object.entries(lenCapByModel)
    .filter(([, v]) => v != null && v !== "")
    .map(([m, v]) => {
      const lbl = isSI
        ? `${Math.round(parseFloat(v) * 300)} mm`
        : `${v} ft`;
      return `${m.replace(/^Model\s*/, "")} ≤ ${lbl}`;
    });
  if (lenCapPairs.length) {
    rows.push({
      group: "Advanced",
      label: "Length Cap (per model)",
      value: lenCapPairs.join("  ·  "),
    });
  }

  // PA inputs (only for projects that consume primary air)
  const paEatDb = dc.pa_eat_db_f ?? adv.pa_eat_db_f;
  const paEatRh = dc.pa_eat_rh ?? adv.pa_eat_rh;
  const hasPA = (paEatDb != null && paEatDb !== "") || (paEatRh != null && paEatRh !== "");
  if (hasPA) {
    const dbStr = (paEatDb != null && paEatDb !== "") ? `${fmtT(paEatDb, isSI)} ${TU(isSI)}` : "—";
    const rhStr = (paEatRh != null && paEatRh !== "") ? `${paEatRh}%` : "—";
    rows.push({
      group: "Advanced",
      label: "PA Entering Air (DB / RH)",
      value: `${dbStr} / ${rhStr}`,
    });
  }

  const paFlow = adv.pa_flow_cfm;
  if (paFlow != null && paFlow !== "" && isFinite(p(paFlow))) {
    const flowVal = isSI
      ? `${(p(paFlow) * 0.4719474).toFixed(1)} L/s`
      : `${p(paFlow).toFixed(0)} CFM`;
    rows.push({
      group: "Advanced",
      label: "PA Flow",
      value: flowVal,
    });
  }

  const paStatic = p(adv.pa_pressure_in_wg);
  if (isFinite(paStatic) && paStatic > 0) {
    const staticVal = isSI
      ? `${(paStatic * 248.84).toFixed(0)} Pa`
      : `${paStatic.toFixed(2)} in. wg`;
    rows.push({
      group: "Advanced",
      label: "PA External Static Pressure",
      value: staticVal,
    });
  }

  return rows;
}

// ---- Group colours ----
const GROUP_BG = {
  Outdoor:  "#e0f2fe", // sky-100
  Indoor:   "#f0fdf4", // green-50
  Water:    "#eff6ff", // blue-50
  Advanced: "#fefce8", // yellow-50
};
const GROUP_LABEL_COLOR = {
  Outdoor:  "#0369a1", // sky-700
  Indoor:   "#166534", // green-800
  Water:    "#1d4ed8", // blue-700
  Advanced: "#92400e", // amber-800
};

/**
 * Returns an HTML string for the Design Conditions section suitable for
 * renderHTMLToPdf(). Uses the same 11px / 10px typescale as projectInfoBlock().
 *
 * @param {object} project
 * @param {boolean} isSI
 * @param {string|null} [model] — unit model; suppresses Outdoor + heat-recovery rows for non-Model-N
 */
export function designConditionsHTML(project, isSI = false, model = null) {
  const rows = buildDesignConditionsRows(project, isSI, model);
  if (!rows.length) return "";

  // Group rows by section
  const groups = ["Outdoor", "Indoor", "Water", "Advanced"];
  const grouped = {};
  groups.forEach((g) => { grouped[g] = []; });
  rows.forEach((r) => { (grouped[r.group] || (grouped["Advanced"] = grouped["Advanced"] || [])).push(r); });

  const groupSections = groups
    .filter((g) => grouped[g] && grouped[g].length)
    .map((g) => {
      const bg = GROUP_BG[g] || "#f8fafc";
      const lc = GROUP_LABEL_COLOR[g] || "#0f172a";
      const trs = grouped[g]
        .map((row) => `
          <tr>
            <td style="padding:3px 6px;vertical-align:middle;color:#475569;width:45%;font-size:9px;border-bottom:1px solid #f1f5f9;">${row.label}</td>
            <td style="padding:3px 6px;vertical-align:middle;font-weight:600;font-size:9px;border-bottom:1px solid #f1f5f9;font-variant-numeric:tabular-nums;">${row.value}</td>
          </tr>`)
        .join("");
      return `
        <tr>
          <td colspan="2" style="padding:4px 6px;vertical-align:middle;background:${bg};font-size:8px;font-weight:700;color:${lc};letter-spacing:0.5px;text-transform:uppercase;">${g}</td>
        </tr>
        ${trs}`;
    })
    .join("");

  return `
    <div style="font-size:11px;margin-bottom:10px;">
      <div style="font-weight:600;background:#0f172a;color:#fff;padding:6px 8px;margin-bottom:2px;font-size:11px;letter-spacing:0.3px;">Design Conditions</div>
      <table style="width:100%;border-collapse:collapse;">
        ${groupSections}
      </table>
    </div>
  `;
}

/**
 * Draw the Design Conditions section directly onto a jsPDF instance.
 * Draws a section heading followed by a compact 2-column table.
 * Returns the new Y cursor (mm) after the section.
 *
 * @param {jsPDF} pdf
 * @param {object} project
 * @param {boolean} isSI
 * @param {number} startY  — top of the section in mm
 * @param {object} [opts]
 * @param {number} [opts.margin=14]   — left/right margin in mm
 * @param {number} [opts.contentW]    — override content width
 * @param {string|null} [opts.model]  — unit model; suppresses Outdoor + heat-recovery rows for non-Model-N
 * @returns {number} new Y cursor
 */
export function drawDesignConditionsToPdf(pdf, project, isSI, startY, opts = {}) {
  const rows = buildDesignConditionsRows(project, isSI, opts.model ?? null);
  if (!rows.length) return startY;

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = opts.margin ?? 14;
  const contentW = opts.contentW ?? (pageW - margin * 2);

  let y = startY;
  const rowH = 6;
  const headH = 8;
  const groupHeadH = 5.5;

  // ---- Section heading ----
  pdf.setFillColor(15, 23, 42); // #0f172a
  pdf.rect(margin, y, contentW, headH, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont(undefined, "bold");
  pdf.text("DESIGN CONDITIONS", margin + 3, y + 5.5);
  y += headH + 1;

  // Group colour fills (R,G,B)
  const GROUP_FILLS = {
    Outdoor:  [224, 242, 254], // sky-100
    Indoor:   [240, 253, 244], // green-50
    Water:    [239, 246, 255], // blue-50
    Advanced: [254, 252, 232], // yellow-50
  };
  const GROUP_TEXT_COLORS = {
    Outdoor:  [3, 105, 161],   // sky-700
    Indoor:   [22, 101, 52],   // green-800
    Water:    [29, 78, 216],   // blue-700
    Advanced: [146, 64, 14],   // amber-800
  };

  const labelColW = contentW * 0.55;

  const groups = ["Outdoor", "Indoor", "Water", "Advanced"];
  const groupedRows = {};
  groups.forEach((g) => { groupedRows[g] = []; });
  rows.forEach((r) => { groupedRows[r.group]?.push(r); });

  groups.forEach((grp) => {
    const grpRows = groupedRows[grp];
    if (!grpRows || !grpRows.length) return;

    // Check if we need a new page
    const totalH = groupHeadH + grpRows.length * rowH;
    if (y + totalH > pageH - 10) {
      pdf.addPage();
      y = margin;
    }

    // Group heading bar
    const [fr, fg, fb] = GROUP_FILLS[grp] || [248, 250, 252];
    const [tr, tg, tb] = GROUP_TEXT_COLORS[grp] || [15, 23, 42];
    pdf.setFillColor(fr, fg, fb);
    pdf.rect(margin, y, contentW, groupHeadH, "F");
    pdf.setTextColor(tr, tg, tb);
    pdf.setFontSize(6.5);
    pdf.setFont(undefined, "bold");
    pdf.text(grp.toUpperCase(), margin + 3, y + groupHeadH - 1.5);
    y += groupHeadH;

    // Rows
    grpRows.forEach((row, ri) => {
      // Row background — alternate very subtle
      if (ri % 2 === 1) {
        pdf.setFillColor(248, 250, 252); // #f8fafc
        pdf.rect(margin, y, contentW, rowH, "F");
      }

      pdf.setTextColor(71, 85, 105); // #475569
      pdf.setFontSize(7.5);
      pdf.setFont(undefined, "normal");
      pdf.text(row.label, margin + 3, y + rowH - 1.8);

      pdf.setTextColor(15, 23, 42); // #0f172a
      pdf.setFont(undefined, "bold");
      pdf.text(String(row.value), margin + labelColW + 2, y + rowH - 1.8);

      // Row bottom border
      pdf.setDrawColor(226, 232, 240); // #e2e8f0
      pdf.setLineWidth(0.15);
      pdf.line(margin, y + rowH, margin + contentW, y + rowH);

      y += rowH;
    });
  });

  // Outer border around the whole section
  pdf.setDrawColor(15, 23, 42);
  pdf.setLineWidth(0.4);
  pdf.rect(margin, startY, contentW, y - startY);

  return y + 3; // 3mm gap after section
}
